/**
 * Prerequisite Definitions — Learning Dependency Graph edges.
 *
 * ARCHITECTURE v4.0 — Phase 1, Step 1.6
 *
 * These define the "must master X before Y" relationships between families.
 * Rules:
 * - Prerequisites must have lower or equal tier (tier monotonicity)
 * - Cross-module prerequisites are allowed but should be rare and documented
 * - 'hard' = must master before proceeding; 'soft' = recommended but not blocking
 *
 * STATUS: All 10 modules defined. ~300 edges.
 * LDG Audit (16 mrt 2026): 11 prerequisite-wijzigingen doorgevoerd.
 * REK module (17 mrt 2026): 3 GET bewerkingen prereqs verwijderd, REK prereqs toegevoegd.
 * LDG Didactisch Audit (6 apr 2026): 12 quick fixes doorgevoerd (QF-1 t/m QF-12).
 * LDG Didactisch Audit Ronde 2 (7 apr 2026): QF-13 tier fix + QF-15/16/17 nieuwe edges.
 * GAP 8: Full ~400 edges require curriculum designer collaboration.
 */

import type { PrerequisiteDefinition } from './types'

// ─── GET — Getalbegrip (50 families) ──────────────────────────────────────────

const GET_PREREQUISITES: PrerequisiteDefinition[] = [
  // Skill: positiestelsel — linear progression
  {
    familyId: 'GET-GROOT-LEZEN',
    prerequisites: ['GET-GROOT-POS'],
    notes: 'Must understand place value before reading large numbers',
  },
  {
    familyId: 'GET-GROOT-VERGELIJK',
    prerequisites: ['GET-GROOT-LEZEN'],
    notes: 'Must read numbers before comparing them',
  },
  {
    familyId: 'GET-GROOT-AFRONDEN',
    prerequisites: ['GET-GROOT-VERGELIJK', 'GET-ROND-BASIS'],
    notes:
      'Comparing helps understand rounding. G-2: Basic rounding before thousands rounding',
  },
  {
    familyId: 'GET-ROND-BASIS',
    prerequisites: ['GET-GROOT-AFRONDEN'],
    notes: 'Basic rounding before thousands rounding',
  },
  {
    familyId: 'GET-GROOT-SCHATTEN',
    prerequisites: ['GET-ROND-BASIS'],
    notes: 'Rounding is foundation for estimation',
  },

  // Skill: breuken — branching progression
  {
    familyId: 'GET-BREUK-LEZEN',
    prerequisites: ['GET-BREUK-BASIS'],
    notes: 'Visual understanding before notation',
  },
  {
    familyId: 'GET-BREUK-GEL-PLUS',
    prerequisites: ['GET-BREUK-LEZEN'],
    notes: 'Must read fractions before adding them',
  },
  {
    familyId: 'GET-BREUK-GEL-MIN',
    prerequisites: ['GET-BREUK-GEL-PLUS'],
    notes: 'Addition before subtraction',
  },
  {
    familyId: 'GET-VERGELIJK',
    prerequisites: ['GET-BREUK-LEZEN'],
    notes: 'Must read fractions before comparing',
  },
  {
    familyId: 'GET-BREUK-GETALLENLIJN',
    prerequisites: ['GET-VERGELIJK'],
    notes: 'Comparing helps with number line placement',
  },
  {
    familyId: 'GET-BREUK-VEREENVOUDIG',
    prerequisites: ['GET-VERGELIJK'],
    notes: 'Comparing fractions leads to simplification',
  },
  {
    familyId: 'GET-BREUK-UITBREIDEN',
    prerequisites: ['GET-BREUK-VEREENVOUDIG'],
    notes: 'Simplification and expansion are inverse operations',
  },
  {
    familyId: 'GET-BREUK-ONG-PLUS',
    prerequisites: ['GET-BREUK-GEL-PLUS', 'GET-BREUK-UITBREIDEN'],
    notes: 'Need same-denominator addition + expansion skill',
  },
  {
    familyId: 'GET-BREUK-MIN',
    prerequisites: ['GET-BREUK-ONG-PLUS', 'GET-BREUK-GEL-MIN'],
    notes: 'Unlike subtraction builds on unlike addition',
  },
  {
    familyId: 'GET-BREUK-KEER',
    prerequisites: ['GET-BREUK-VEREENVOUDIG', 'GET-BREUK-ONG-PLUS'],
    notes:
      'QF-25: Simplification needed for multiplication results + addition before multiplication (didactic ordering)',
  },
  {
    familyId: 'GET-BREUK-GEMENGD',
    prerequisites: ['GET-BREUK-ONG-PLUS'],
    notes: 'Mixed numbers arise from improper fractions in addition',
  },
  {
    familyId: 'GET-BREUK-DEEL',
    prerequisites: ['GET-BREUK-KEER'],
    notes: 'Division = multiply by reciprocal',
  },

  // Skill: kommagetallen — builds on positiestelsel
  {
    familyId: 'GET-KOMMA-WAARDE',
    prerequisites: ['GET-GROOT-POS'],
    notes: 'Place value extends to decimals',
  },
  {
    familyId: 'GET-KOMMA-LEZEN',
    prerequisites: ['GET-KOMMA-WAARDE'],
    notes: 'Understand value before reading/writing',
  },
  {
    familyId: 'GET-KOMMA-VERGELIJK',
    prerequisites: ['GET-KOMMA-LEZEN'],
    notes: 'Must read decimals before comparing',
  },
  {
    familyId: 'GET-KOMMA-GETALLENLIJN',
    prerequisites: ['GET-KOMMA-VERGELIJK'],
    notes: 'Comparing helps with number line placement',
  },
  {
    familyId: 'GET-KOMMA-PLUS',
    prerequisites: ['GET-KOMMA-LEZEN'],
    notes: 'Must read decimals before adding',
  },
  {
    familyId: 'GET-KOMMA-MIN',
    prerequisites: ['GET-KOMMA-PLUS'],
    notes: 'Addition before subtraction',
  },
  {
    familyId: 'GET-KOMMA-KEER',
    prerequisites: ['GET-KOMMA-PLUS'],
    notes: 'Addition understanding before multiplication',
  },
  {
    familyId: 'GET-KOMMA-DEEL',
    prerequisites: ['GET-KOMMA-KEER'],
    notes: 'Multiplication before division',
  },
  {
    familyId: 'GET-DEEL-KOMMA',
    prerequisites: ['GET-KOMMA-DEEL'],
    notes: 'Division of decimals before dividing by decimals',
  },
  {
    familyId: 'GET-KOMMA-AFRONDEN',
    prerequisites: ['GET-KOMMA-VERGELIJK', 'GET-GROOT-AFRONDEN'],
    notes: 'Decimal comparison + rounding concept',
  },

  // Skill: procenten — builds on breuken + kommagetallen
  {
    familyId: 'GET-PROC-BASIS',
    prerequisites: ['GET-BREUK-VEREENVOUDIG', 'GET-KOMMA-WAARDE'],
    notes: 'Percent = fraction = decimal (three-way link)',
  },
  {
    familyId: 'GET-PROC-VAN',
    prerequisites: ['GET-PROC-BASIS'],
    notes: 'Basic percent understanding before calculation',
  },
  {
    familyId: 'GET-VERH-BREUK-PROC-KOPPEL',
    prerequisites: ['GET-PROC-BASIS'],
    notes: 'Deepens the three-way link',
  },
  {
    familyId: 'GET-PROC-NIEUW',
    prerequisites: ['GET-PROC-VAN'],
    notes: 'Calculate percentage before applying discount/markup',
  },
  {
    familyId: 'GET-PROC-SAMENGESTELD',
    prerequisites: ['GET-PROC-VAN'],
    notes: 'Compound percentage builds on basic percentage-of-amount',
  },
  {
    familyId: 'GET-PROC-TOENAME',
    prerequisites: ['GET-PROC-NIEUW'],
    notes: 'Price changes before percentage increase',
  },
  {
    familyId: 'GET-PROC-AFNAME',
    prerequisites: ['GET-PROC-TOENAME'],
    notes: 'Increase before decrease (same pattern)',
  },
  {
    familyId: 'GET-PROC-TERUG',
    prerequisites: ['GET-PROC-NIEUW'],
    notes: 'Forward calculation before reverse',
  },
  {
    familyId: 'GET-PROC-BTW',
    prerequisites: ['GET-PROC-TERUG'],
    notes: 'Reverse calculation is foundation for BTW exclusive',
  },

  // Skill: verhoudingen — builds on breuken + procenten
  {
    familyId: 'GET-VERH-RECEPT',
    prerequisites: ['GET-PROC-VAN'],
    notes: 'Percentage of amount is foundation for ratio',
  },
  {
    familyId: 'GET-VERH-SCHAAL',
    prerequisites: ['GET-VERH-RECEPT'],
    notes: 'Recipe ratios before ratio tables',
  },
  {
    familyId: 'GET-VERH-SCHAAL-KAART',
    prerequisites: ['GET-VERH-SCHAAL'],
    notes: 'Ratio tables before map scale',
  },
  {
    familyId: 'GET-VERH-VERDELEN',
    prerequisites: ['GET-VERH-SCHAAL'],
    notes: 'Ratio tables before dividing in ratio',
  },

  // Skill: negatieve_getallen — builds on kommagetallen
  {
    familyId: 'GET-NEGATIEF',
    prerequisites: ['GET-KOMMA-GETALLENLIJN'],
    notes: 'Number line understanding extends to negatives',
  },
  {
    familyId: 'GET-NEGATIEF-PLUS',
    prerequisites: ['GET-NEGATIEF'],
    notes: 'Understand negatives before adding with them',
  },
  {
    familyId: 'GET-NEGATIEF-MIN',
    prerequisites: ['GET-NEGATIEF-PLUS'],
    notes: 'Addition before subtraction with negatives',
  },

  // Skill: machten_wortels — builds on REK-VOLGORDE (was GET-VOLGORDE-BEWERKING)
  {
    familyId: 'GET-MACHT',
    prerequisites: ['REK-VOLGORDE'],
    notes:
      'Order of operations includes exponents (cross-module: REK-VOLGORDE, was GET-VOLGORDE-BEWERKING)',
  },
  {
    familyId: 'GET-MACHT-GRONDTAL',
    prerequisites: ['GET-MACHT'],
    notes: 'Basic exponents before base variations',
  },
  {
    familyId: 'GET-WORTEL',
    prerequisites: ['GET-MACHT'],
    notes: 'Exponents before roots (inverse operation)',
  },

  // Skill: wetenschappelijk — builds on machten
  {
    familyId: 'GET-WETENSCHAPPELIJK',
    prerequisites: ['GET-MACHT', 'GET-KOMMA-KEER'],
    notes: 'Exponents + decimal multiplication for scientific notation',
  },
  {
    familyId: 'GET-WETENSCHAP-REKEN',
    prerequisites: ['GET-WETENSCHAPPELIJK'],
    notes: 'Read notation before calculating with it',
  },

  // QF-6: Slider variant families — link to base families
  {
    familyId: 'GET-BREUK-BASIS-SLIDER',
    prerequisites: ['GET-BREUK-BASIS'],
    notes: 'QF-6: Slider variant must master base family first',
  },
  {
    familyId: 'GET-GROOT-SCHATTEN-SLIDER',
    prerequisites: ['GET-GROOT-SCHATTEN'],
    notes: 'QF-6: Slider variant must master base family first',
  },
  {
    familyId: 'GET-KOMMA-GETALLENLIJN-SLIDER',
    prerequisites: ['GET-KOMMA-GETALLENLIJN'],
    notes: 'QF-6: Slider variant must master base family first',
  },
]

// ─── REK — Basisrekenen (17 families) ─────────────────────────────────────────
// Created 17 maart 2026 (REK module Fase 0)

const REK_PREREQUISITES: PrerequisiteDefinition[] = [
  // Tafels chain
  { familyId: 'REK-TAFELS-TOEPASSEN', prerequisites: ['REK-TAFELS-BASIS'] },
  {
    familyId: 'REK-TAFELS-UITBREIDEN',
    prerequisites: ['REK-TAFELS-BASIS'],
    notes:
      'QF-4: Direct dependency on BASIS, not TOEPASSEN. Uitbreiden (11×, 12×) is a pure recall skill, not context-dependent.',
  },

  // Cijferend chain
  { familyId: 'REK-CIJF-MIN', prerequisites: ['REK-CIJF-PLUS'] },
  {
    familyId: 'REK-CIJF-KEER',
    prerequisites: ['REK-CIJF-MIN', 'REK-TAFELS-BASIS'],
  },
  { familyId: 'REK-CIJF-DEEL', prerequisites: ['REK-CIJF-KEER'] },

  // Handig chain
  { familyId: 'REK-HANDIG-PLUS-MIN', prerequisites: ['REK-CIJF-MIN'] },
  {
    familyId: 'REK-HANDIG-KEER',
    prerequisites: ['REK-TAFELS-UITBREIDEN', 'REK-CIJF-KEER'],
  },
  {
    familyId: 'REK-HANDIG-DEEL',
    prerequisites: ['REK-HANDIG-KEER', 'REK-CIJF-DEEL'],
  },

  // Schatten (gemigreerd van GET-SCHATSTRATEGIE)
  {
    familyId: 'REK-SCHATTEN',
    prerequisites: ['REK-HANDIG-PLUS-MIN'],
    notes:
      'Migrated from GET-SCHATSTRATEGIE. Hard prereq on HANDIG-PLUS-MIN for strategy awareness.',
  },
  {
    familyId: 'REK-SCHATTEN',
    prerequisites: ['GET-ROND-BASIS'],
    type: 'soft',
    notes:
      'Didactisch audit 2026: afronden is fundamenteel voor schatten. Soft prereq — niet blokkerend maar sterk aanbevolen.',
  },

  // Volgorde (gemigreerd van GET-VOLGORDE-BEWERKING)
  {
    familyId: 'REK-VOLGORDE',
    prerequisites: ['REK-CIJF-KEER', 'REK-CIJF-DEEL'],
    notes:
      'QF-3: All four operations needed for order of operations (was only REK-CIJF-KEER)',
  },

  // Context chain
  {
    familyId: 'REK-CONTEXT-PLUS-MIN',
    prerequisites: ['REK-HANDIG-PLUS-MIN'],
    notes:
      'Didactisch audit 2026: GEMENGD-BASIS verwijderd als prereq. In schoolmethodes (Pluspunt, WiG) komen verhaaltjessommen vóór gemengde oefeningen. HANDIG-PLUS-MIN volstaat als rekenvaardigheidsbasis.',
  },
  {
    familyId: 'REK-CONTEXT-KEER-DEEL',
    prerequisites: ['REK-HANDIG-KEER', 'REK-CONTEXT-PLUS-MIN'],
  },
  {
    familyId: 'REK-CONTEXT-MEERSTAPS',
    prerequisites: ['REK-CONTEXT-KEER-DEEL'],
  },

  // Gemengd
  {
    familyId: 'REK-GEMENGD-BASIS',
    prerequisites: ['REK-HANDIG-PLUS-MIN', 'REK-CIJF-KEER'],
  },
  {
    familyId: 'REK-GEMENGD-TOETS',
    prerequisites: [
      'REK-CONTEXT-MEERSTAPS',
      'REK-HANDIG-DEEL',
      'REK-VOLGORDE',
      'REK-GEMENGD-BASIS',
    ],
    notes:
      'Tier 12 (E7+) > all prereqs: MEERSTAPS(11), HANDIG-DEEL(9), VOLGORDE(9). Capstone family. R-5: Toetsvorm is een moeilijkere variant van gemengd.',
  },
]

// ─── MET — Metriek & Meetkunde (39 families) ─────────────────────────────────
// Starter set — needs curriculum designer review

const MET_PREREQUISITES: PrerequisiteDefinition[] = [
  { familyId: 'MET-LEN-SCHATTEN', prerequisites: ['MET-LEN-OMREKEN'] },
  { familyId: 'MET-LEN-NAUWKEURIG', prerequisites: ['MET-LEN-METEN'] },
  { familyId: 'MET-LEN-CONTEXT', prerequisites: ['MET-LEN-OMREKEN'] },
  {
    familyId: 'MET-GEW-OMREKEN',
    prerequisites: ['MET-LEN-OMREKEN'],
    notes: 'Same metric staircase pattern',
  },
  {
    familyId: 'MET-GEW-SCHATTEN',
    prerequisites: ['MET-GEW-WEGEN', 'MET-GEW-OMREKEN'],
    notes: 'Must know units (omrekenen) before estimating weight',
  },
  { familyId: 'MET-GEW-CONTEXT', prerequisites: ['MET-GEW-OMREKEN'] },
  {
    familyId: 'MET-INH-OMREKEN',
    prerequisites: ['MET-LEN-OMREKEN'],
    notes: 'Same metric staircase pattern',
  },
  {
    familyId: 'MET-INH-BLOK',
    prerequisites: ['MET-OPP-RECHT'],
    notes: 'Area before volume',
  },
  {
    familyId: 'MET-INH-LITER',
    prerequisites: ['MET-INH-OMREKEN', 'MET-INH-BLOK'],
  },
  { familyId: 'MET-INH-OMREKEN-KUBIEK', prerequisites: ['MET-INH-LITER'] },
  { familyId: 'MET-INH-CITER', prerequisites: ['MET-INH-LITER'] },
  { familyId: 'MET-INH-SCHATTEN', prerequisites: ['MET-INH-OMREKEN'] },
  { familyId: 'MET-OMTREK-RECHT', prerequisites: ['MET-LEN-OMREKEN'] },
  {
    familyId: 'MET-OPP-RECHT',
    prerequisites: ['MET-LEN-OMREKEN', 'MET-OMTREK-RECHT'],
    notes: 'QF-9: Omtrek (1D) before oppervlakte (2D)',
  },
  { familyId: 'MET-OPP-VIERKANT', prerequisites: ['MET-OPP-RECHT'] },
  {
    familyId: 'MET-OPP-PARALLELLOGRAM',
    prerequisites: ['MET-OPP-RECHT'],
    notes:
      'QF-8: Parallellogram (b×h) before driehoek (½×b×h) — driehoek derives from parallellogram',
  },
  {
    familyId: 'MET-OPP-DRIEHOEK',
    prerequisites: ['MET-OPP-PARALLELLOGRAM', 'MET-OPP-RECHT'],
    notes:
      'QF-8: Triangle formula (½×b×h) derived as half of parallellogram. M-8: Oppervlakte driehoek bouwt voort op rechthoek.',
  },
  { familyId: 'MET-OPP-SAMENGESTELD', prerequisites: ['MET-OPP-DRIEHOEK'] },
  { familyId: 'MET-OPP-LAND', prerequisites: ['MET-OPP-RECHT'] },
  { familyId: 'MET-OPP-OMREKENEN', prerequisites: ['MET-OPP-LAND'] },
  { familyId: 'MET-MEETK-DIAMETER', prerequisites: ['MET-OPP-RECHT'] },
  { familyId: 'MET-MEETK-OMTREK', prerequisites: ['MET-MEETK-DIAMETER'] },
  {
    familyId: 'MET-MEETK-OPP',
    prerequisites: ['MET-MEETK-OMTREK', 'MET-MEETK-DIAMETER'],
  },
  { familyId: 'MET-MEETK-CIRKEL-DEEL', prerequisites: ['MET-MEETK-OPP'] },
  {
    familyId: 'MET-HOEK-METEN',
    prerequisites: ['MET-LEN-METEN', 'MET-HOEK-SOORTEN'],
    notes:
      'Basic measuring skills before angle measurement. M-4: Je moet hoeksoorten kennen vóór je ze gaat meten.',
  },
  { familyId: 'MET-HOEK-TEKENEN', prerequisites: ['MET-HOEK-METEN'] },
  { familyId: 'MET-HOEK-SOORTEN', prerequisites: ['MET-HOEK-METEN'] },
  {
    familyId: 'MET-HOEK-BEREKENEN',
    prerequisites: ['MET-HOEK-SOORTEN', 'MET-HOEK-METEN'],
  },
  { familyId: 'MET-DRIEHOEK-SOORTEN', prerequisites: ['MET-HOEK-SOORTEN'] },
  {
    familyId: 'MET-SYMMETRIE',
    prerequisites: ['MET-OPP-RECHT'],
    notes: 'Understanding basic shapes before symmetry',
  },
  {
    familyId: 'MET-COORDINATEN',
    prerequisites: ['MET-LEN-METEN'],
    notes:
      'QF-4: Coordinates relate to number lines and measuring, not symmetry (was MET-SYMMETRIE)',
  },
  { familyId: 'MET-SCHAAL-BASIS', prerequisites: ['MET-LEN-OMREKEN'] },
  { familyId: 'MET-SCHAAL-LENGTE', prerequisites: ['MET-SCHAAL-BASIS'] },
  {
    familyId: 'MET-SCHAAL-OPP',
    prerequisites: ['MET-SCHAAL-LENGTE', 'MET-OPP-OMREKENEN'],
  },
  { familyId: 'MET-TIJD-NAUWKEURIG', prerequisites: ['MET-TIJD-METEN'] },

  // QF-6: Categorize variant family
  {
    familyId: 'MET-HOEK-SOORTEN-CATEGORIZE',
    prerequisites: ['MET-HOEK-SOORTEN'],
    notes: 'QF-6: Categorize variant must master base family first',
  },
]

// ─── TIJD — Klokkijken, Tijdrekenen & Snelheid (16 families) ─────────────────
// Fase 0 TGL Split: extracted from TGL_PREREQUISITES.
// ACTIVATED 18 maart 2026 (Fase 0 TGL Split).
// T8 Phase 2 (9 apr 2026): familyIds renamed TGL-TIJD-* → TIJD-* to match seed JSONs.

const TIJD_PREREQUISITES: PrerequisiteDefinition[] = [
  { familyId: 'TIJD-DIGITAAL', prerequisites: ['TIJD-ANALOOG'] },
  {
    familyId: 'TIJD-ANALOOG-GEDETAILLEERD',
    prerequisites: ['TIJD-ANALOOG'],
  },
  {
    familyId: 'TIJD-CONVERSIE',
    prerequisites: ['TIJD-DIGITAAL', 'TIJD-ANALOOG-GEDETAILLEERD'],
  },
  { familyId: 'TIJD-12-24', prerequisites: ['TIJD-CONVERSIE'] },
  { familyId: 'TIJD-TIJDZONES', prerequisites: ['TIJD-12-24'] },
  { familyId: 'TIJD-DUUR', prerequisites: ['TIJD-CONVERSIE'] },
  {
    familyId: 'TIJD-SAMENGESTELD',
    prerequisites: ['TIJD-DUUR', 'TIJD-OMREKENEN'],
    notes:
      'Compound time calculations build on basic duration. T-5: Samengestelde tijdberekeningen vereisen omrekenen.',
  },
  { familyId: 'TIJD-PLANNING', prerequisites: ['TIJD-DUUR'] },
  { familyId: 'TIJD-SCHEMA', prerequisites: ['TIJD-PLANNING'] },
  { familyId: 'TIJD-DIAGRAM', prerequisites: ['TIJD-SCHEMA'] },
  {
    familyId: 'TIJD-KALENDER',
    prerequisites: ['TIJD-DUUR', 'TIJD-OMREKENEN'],
    notes: 'QF-11: Kalenderberekeningen (dagen→weken→maanden) = tijdomrekenen',
  },
  { familyId: 'TIJD-OMREKENEN', prerequisites: ['TIJD-DUUR'] },
  { familyId: 'TIJD-SNELHEID', prerequisites: ['TIJD-OMREKENEN', 'TIJD-DUUR'] },
  { familyId: 'TIJD-AFSTAND', prerequisites: ['TIJD-SNELHEID'] },
  { familyId: 'TIJD-GEMIDDELDE', prerequisites: ['TIJD-AFSTAND'] },
]

// ─── GELD — Geld Tellen, Wisselgeld, Korting & BTW (15 families) ─────────────
// Fase 0 TGL Split: extracted from TGL_PREREQUISITES.
// ACTIVATED 18 maart 2026 (Fase 0 TGL Split).
// T8 Phase 2 (9 apr 2026): familyIds renamed TGL-GELD-* → GELD-* to match seed JSONs.

const GELD_PREREQUISITES: PrerequisiteDefinition[] = [
  { familyId: 'GELD-TELLEN', prerequisites: [] },
  { familyId: 'GELD-MAAK', prerequisites: ['GELD-TELLEN'] },
  { familyId: 'GELD-TERUG', prerequisites: ['GELD-MAAK'] },
  { familyId: 'GELD-WISSEL', prerequisites: ['GELD-TERUG'] },
  { familyId: 'GELD-KOMMA', prerequisites: ['GELD-WISSEL'] },
  { familyId: 'GELD-WISSELEN', prerequisites: ['GELD-KOMMA'] },
  { familyId: 'GELD-BOODSCHAPPEN', prerequisites: ['GELD-KOMMA'] },
  { familyId: 'GELD-KOOPJES', prerequisites: ['GELD-BOODSCHAPPEN'] },
  { familyId: 'GELD-BUDGET', prerequisites: ['GELD-KOOPJES'] },
  { familyId: 'GELD-KORTING', prerequisites: ['GELD-KOOPJES'] },
  { familyId: 'GELD-PRIJS-PER', prerequisites: ['GELD-KOOPJES'] },
  { familyId: 'GELD-SPAREN', prerequisites: ['GELD-BUDGET'] },
  { familyId: 'GELD-RENTE-PROC', prerequisites: ['GELD-SPAREN'] },
  { familyId: 'GELD-BTW', prerequisites: ['GELD-KORTING'] },
  {
    familyId: 'GELD-TERUGREKENEN',
    prerequisites: ['GELD-BTW', 'GELD-KORTING'],
    notes: 'Terugrekenen vereist forward percentage (korting) + BTW begrip.',
  },
  {
    familyId: 'GELD-TERUGREKENEN',
    prerequisites: ['GET-PROC-TERUG'],
    type: 'soft',
    notes:
      'GET procenten terugrekenen is aanbevolen maar niet blokkerend voor GELD terugrekenen.',
  },
  { familyId: 'GELD-TERUGREKENEN', prerequisites: ['GELD-BTW'] },
]

// ─── SWW — Spelling Werkwoorden (24 families) ────────────────────────────────

const SWW_PREREQUISITES: PrerequisiteDefinition[] = [
  { familyId: 'SWW-TT-MEERVOUD', prerequisites: ['SWW-TT-STAM'] },
  { familyId: 'SWW-TT-ZIJN-HEBBEN', prerequisites: ['SWW-TT-STAM'] },
  { familyId: 'SWW-TT-INV-JIJ', prerequisites: ['SWW-TT-STAM'] },
  { familyId: 'SWW-TT-GEBIEDENDE', prerequisites: ['SWW-TT-STAM'] },
  { familyId: 'SWW-TT-DSTAM', prerequisites: ['SWW-TT-INV-JIJ'] },
  { familyId: 'SWW-TT-INV-TRICK', prerequisites: ['SWW-TT-DSTAM'] },
  { familyId: 'SWW-VT-STERK', prerequisites: ['SWW-TT-STAM'] },
  { familyId: 'SWW-VT-ZIJN-HEBBEN', prerequisites: ['SWW-VT-STERK'] },
  { familyId: 'SWW-VT-ZWAK-KOF', prerequisites: ['SWW-TT-STAM'] },
  { familyId: 'SWW-VT-ONREGELMATIG', prerequisites: ['SWW-VT-STERK'] },
  { familyId: 'SWW-VT-VZFS', prerequisites: ['SWW-VT-ZWAK-KOF'] },
  { familyId: 'SWW-VT-ZULLEN-KUNNEN', prerequisites: ['SWW-VT-STERK'] },
  { familyId: 'SWW-VT-DUBBEL', prerequisites: ['SWW-VT-ZWAK-KOF'] },
  { familyId: 'SWW-VD-KOF', prerequisites: ['SWW-VT-ZWAK-KOF'] },
  { familyId: 'SWW-VD-STERK', prerequisites: ['SWW-VT-STERK', 'SWW-VD-KOF'] },
  { familyId: 'SWW-VD-ZONDER-GE', prerequisites: ['SWW-VD-KOF'] },
  { familyId: 'SWW-VD-HULPWW', prerequisites: ['SWW-VD-KOF'] },
  {
    familyId: 'SWW-VD-HOMOFOON',
    prerequisites: ['SWW-VD-KOF', 'SWW-TT-DSTAM'],
  },
  { familyId: 'SWW-BN-VD-KORT', prerequisites: ['SWW-VD-KOF'] },
  { familyId: 'SWW-BN-VD-DUBBEL', prerequisites: ['SWW-BN-VD-KORT'] },
  {
    familyId: 'SWW-LEEN-ENG-STAM',
    prerequisites: ['SWW-TT-STAM', 'SWW-VT-ZWAK-KOF'],
    notes:
      'Kofschip rule needed for English verb conjugation in Dutch (to game → gamede/gamed)',
  },
  {
    familyId: 'SWW-LEEN-ENG-KOF',
    prerequisites: ['SWW-LEEN-ENG-STAM', 'SWW-VT-ZWAK-KOF'],
  },
  { familyId: 'SWW-LEEN-FRANS', prerequisites: ['SWW-VT-VZFS'] },

  // QF-6: Error-spotting variant family
  {
    familyId: 'SWW-TT-INV-JIJ-ERROR',
    prerequisites: ['SWW-TT-INV-JIJ'],
    notes: 'QF-6: Error-spotting variant must master base family first',
  },
]

// ─── SPR — Spelling Regels (20 families) ─────────────────────────────────────

const SPR_PREREQUISITES: PrerequisiteDefinition[] = [
  {
    familyId: 'SPR-KLANK-AA-EE-OO',
    prerequisites: ['SPR-KLANK-JAGER', 'SPR-KLANK-BAKKER'],
  },
  { familyId: 'SPR-KLANK-VERDUBBEL', prerequisites: ['SPR-KLANK-BAKKER'] },
  { familyId: 'SPR-KLANK-VERKORTEN', prerequisites: ['SPR-KLANK-JAGER'] },
  { familyId: 'SPR-KLANK-AU-OU', prerequisites: ['SPR-KLANK-AA-EE-OO'] },
  { familyId: 'SPR-WEET-D-T', prerequisites: [] },
  { familyId: 'SPR-WEET-DT', prerequisites: ['SPR-WEET-D-T'] },
  {
    familyId: 'SPR-WEET-EI-IJ',
    prerequisites: ['SPR-KLANK-AA-EE-OO'],
    type: 'soft',
    notes: 'QF-10: Was completely isolated — klankonderscheid als basis',
  },
  { familyId: 'SPR-LEEN-FRANS', prerequisites: ['SPR-LEEN-C-S-K'] },
  { familyId: 'SPR-LEEN-ENGELS', prerequisites: ['SPR-LEEN-C-S-K'] },
  { familyId: 'SPR-LEEN-LATIJN', prerequisites: ['SPR-LEEN-ENGELS'] },
  { familyId: 'SPR-LEEN-GRIEKS', prerequisites: ['SPR-LEEN-LATIJN'] },
  { familyId: 'SPR-SAM-TUSSEN-S', prerequisites: ['SPR-SAM-TUSSEN-N'] },
  {
    familyId: 'SPR-SAM-TUSSEN-N',
    prerequisites: ['SPR-KLANK-VERDUBBEL'],
    type: 'soft',
    notes:
      'QF-SPR-1 (Didactisch Audit SPR 2026): Begrip medeklinkerverdubbeling (bakker-regel) helpt bij tussen-n herkenning (pannen→pannenkoek). Niet blokkerend.',
  },
  { familyId: 'SPR-LEESTEKEN-PUNT-KOMMA', prerequisites: ['SPR-HOOFDLETTER'] },
  { familyId: 'SPR-TREMA-MV', prerequisites: ['SPR-KLANK-AA-EE-OO'] },
  { familyId: 'SPR-ACCENT', prerequisites: ['SPR-LEEN-FRANS'] },
  // QF-6: Variant families
  {
    familyId: 'SPR-KLANK-JAGER-CATEGORIZE',
    prerequisites: ['SPR-KLANK-JAGER'],
    notes: 'QF-6: Categorize variant must master base family first',
  },
  {
    familyId: 'SPR-WEET-D-T-ERROR',
    prerequisites: ['SPR-WEET-D-T'],
    notes: 'QF-6: Error-spotting variant must master base family first',
  },

  // QF-15: SPR-LEEN-C-S-K was orphan root — klankbewustzijn als basis
  {
    familyId: 'SPR-LEEN-C-S-K',
    prerequisites: ['SPR-KLANK-AA-EE-OO'],
    type: 'soft',
    notes:
      'QF-15: Leenwoorden vereisen klankbewustzijn — C als S/K hangt af van klankonderscheid',
  },
]

// ─── TAA — Taalbegrip (35 families) ──────────────────────────────────────────

const TAA_PREREQUISITES: PrerequisiteDefinition[] = [
  { familyId: 'TAA-ONT-GEZEGDE', prerequisites: ['TAA-ONT-BASIS'] },
  { familyId: 'TAA-ONT-VOORZETSEL', prerequisites: ['TAA-ONT-BASIS'] },
  { familyId: 'TAA-ONT-LV-MV', prerequisites: ['TAA-ONT-GEZEGDE'] },
  { familyId: 'TAA-ONT-INVERSIE', prerequisites: ['TAA-ONT-BASIS'] },
  { familyId: 'TAA-ONT-NAAMWOORDELIJK', prerequisites: ['TAA-ONT-GEZEGDE'] },
  { familyId: 'TAA-ONT-HOOFDZIN', prerequisites: ['TAA-ONT-LV-MV'] },
  { familyId: 'TAA-ONT-BIJZIN', prerequisites: ['TAA-ONT-HOOFDZIN'] },
  { familyId: 'TAA-ONT-BWB', prerequisites: ['TAA-ONT-LV-MV'] },
  { familyId: 'TAA-ONT-BETREKKELIJK', prerequisites: ['TAA-ONT-BIJZIN'] },
  {
    familyId: 'TAA-SOORT-WW',
    prerequisites: ['TAA-SOORT-LID'],
    type: 'soft',
    notes: 'B3: Woordsoorten zijn declaratief — parallel leerbaar',
  },
  {
    familyId: 'TAA-SOORT-BNW',
    prerequisites: ['TAA-SOORT-LID'],
    type: 'soft',
    notes: 'B3: Woordsoorten zijn declaratief — parallel leerbaar',
  },
  {
    familyId: 'TAA-SOORT-BW',
    prerequisites: ['TAA-SOORT-BNW'],
    type: 'soft',
    notes: 'B3: Woordsoorten zijn declaratief — parallel leerbaar',
  },
  {
    familyId: 'TAA-SOORT-TW',
    prerequisites: ['TAA-SOORT-LID'],
    type: 'soft',
    notes: 'B3: Woordsoorten zijn declaratief — parallel leerbaar',
  },
  {
    familyId: 'TAA-SOORT-VZ',
    prerequisites: ['TAA-SOORT-BW'],
    type: 'soft',
    notes: 'B3: Woordsoorten zijn declaratief — parallel leerbaar',
  },
  {
    familyId: 'TAA-SOORT-VNW',
    prerequisites: ['TAA-SOORT-LID'],
    type: 'soft',
    notes:
      'QF-5: Voornaamwoorden vóór voorzetsels (groep 6 vs groep 7). Was: TAA-SOORT-VZ. B3: soft (declaratief)',
  },
  {
    familyId: 'TAA-SOORT-VW',
    prerequisites: ['TAA-SOORT-BW'],
    type: 'soft',
    notes: 'B3: Woordsoorten zijn declaratief — parallel leerbaar',
  },
  { familyId: 'TAA-LEZEN-DETAIL', prerequisites: [] },
  {
    familyId: 'TAA-TEKSTSOORT',
    prerequisites: ['TAA-LEZEN-DETAIL'],
    type: 'soft',
    notes: 'B3: Leesvaardigheden zijn declaratief — parallel leerbaar',
  },
  {
    familyId: 'TAA-LEZEN-VERW',
    prerequisites: ['TAA-LEZEN-DETAIL'],
    type: 'soft',
    notes: 'B3: Leesvaardigheden zijn declaratief — parallel leerbaar',
  },
  {
    familyId: 'TAA-LEZEN-KERN',
    prerequisites: ['TAA-LEZEN-VERW'],
    type: 'soft',
    notes:
      'Verwijswoorden before hoofdgedachte — understanding references helps find main idea. B3: soft (declaratief)',
  },
  {
    familyId: 'TAA-SIGNAALWOORD',
    prerequisites: ['TAA-LEZEN-KERN'],
    type: 'soft',
    notes:
      'Hoofdgedachte before signaalwoorden — signal words are tools to support main idea finding. B3: soft (declaratief)',
  },
  {
    familyId: 'TAA-LEZEN-VOORSPEL',
    prerequisites: ['TAA-SIGNAALWOORD'],
    type: 'soft',
    notes: 'B3: Leesvaardigheden zijn declaratief — parallel leerbaar',
  },
  {
    familyId: 'TAA-LEZEN-SAMENVATTEN',
    prerequisites: ['TAA-LEZEN-KERN'],
    type: 'soft',
    notes: 'B3: Leesvaardigheden zijn declaratief — parallel leerbaar',
  },
  {
    familyId: 'TAA-LEZEN-CONCLUSIE',
    prerequisites: ['TAA-LEZEN-KERN'],
    type: 'soft',
    notes: 'B3: Leesvaardigheden zijn declaratief — parallel leerbaar',
  },
  {
    familyId: 'TAA-LEZEN-STRUCTUUR',
    prerequisites: ['TAA-LEZEN-KERN'],
    type: 'soft',
    notes: 'B3: Leesvaardigheden zijn declaratief — parallel leerbaar',
  },
  {
    familyId: 'TAA-LEZEN-FEIT',
    prerequisites: ['TAA-LEZEN-KERN'],
    type: 'soft',
    notes:
      'Feit/mening builds on hoofdgedachte — moved from CONCLUSIE prereq (LDG Audit: E7 tier). B3: soft (declaratief)',
  },
  {
    familyId: 'TAA-LEZEN-DOEL',
    prerequisites: ['TAA-LEZEN-FEIT', 'TAA-LEZEN-VERB'],
    type: 'soft',
    notes:
      'Author purpose requires both feit/mening and tekstverbanden (LDG Audit). B3: soft (declaratief)',
  },
  {
    familyId: 'TAA-LEZEN-VERB',
    prerequisites: ['TAA-SIGNAALWOORD'],
    type: 'soft',
    notes:
      'Tekstverbanden build on signaalwoorden — CITO-critical, moved to B8 (LDG Audit). B3: soft (declaratief)',
  },
  {
    familyId: 'TAA-ARGUMENTATIE',
    prerequisites: ['TAA-LEZEN-VERB'],
    type: 'soft',
    notes:
      'Argumentatie requires understanding text connections — restructured (LDG Audit). B3: soft (declaratief)',
  },
  { familyId: 'TAA-WOORD-SYNONIEM', prerequisites: [] },
  {
    familyId: 'TAA-WOORD-ANTONIEM',
    prerequisites: ['TAA-WOORD-SYNONIEM'],
    type: 'soft',
    notes: 'B3: Woordenschat is declaratief — parallel leerbaar',
  },
  {
    familyId: 'TAA-WOORD-CONTEXT',
    prerequisites: ['TAA-WOORD-ANTONIEM'],
    type: 'soft',
    notes: 'B3: Woordenschat is declaratief — parallel leerbaar',
  },
  {
    familyId: 'TAA-WOORD-IDIOOM',
    prerequisites: ['TAA-WOORD-CONTEXT'],
    type: 'soft',
    notes: 'B3: Woordenschat is declaratief — parallel leerbaar',
  },

  // QF-6: Variant families — HARD (variant must master base family first)
  {
    familyId: 'TAA-SOORT-WW-CATEGORIZE',
    prerequisites: ['TAA-SOORT-WW'],
    notes: 'QF-6: Categorize variant must master base family first',
  },
  {
    familyId: 'TAA-LEZEN-SAMENVATTING',
    prerequisites: ['TAA-LEZEN-SAMENVATTEN'],
    notes: 'QF-6: Summary variant must master base family first',
  },
]

// ─── TOPO — Topografie (23 families) ─────────────────────────────────────────

const TOPO_PREREQUISITES: PrerequisiteDefinition[] = [
  {
    familyId: 'TOPO-NL-STEDEN',
    prerequisites: ['TOPO-NL-PROV'],
    notes:
      'Grote steden & provinciehoofdsteden — direct na provincies (M6, didactische volgorde)',
  },
  {
    familyId: 'TOPO-NL-STEDEN-REGIO',
    prerequisites: ['TOPO-NL-STEDEN'],
    notes:
      'Steden per provincie & regio — pas na grote steden (graaf-blok: eerst grote steden, dan regio-verdieping)',
  },
  {
    familyId: 'TOPO-NL-BUURLANDEN',
    prerequisites: ['TOPO-NL-PROV', 'TOPO-NL-STEDEN'],
    notes:
      'Buurlanden pas na provincies én grote steden (steden als ankerpunten; M6+ timing)',
  },
  {
    familyId: 'TOPO-NL-WATER',
    prerequisites: ['TOPO-NL-PROV', 'TOPO-NL-STEDEN'],
    notes:
      'Water questions reference provinces and cities (Maas door Maastricht, Rijn door Arnhem)',
  },
  { familyId: 'TOPO-NL-LANDSCHAP', prerequisites: ['TOPO-NL-WATER'] },
  { familyId: 'TOPO-NL-EILANDEN', prerequisites: ['TOPO-NL-WATER'] },
  { familyId: 'TOPO-EU-LAND', prerequisites: ['TOPO-NL-BUURLANDEN'] },
  { familyId: 'TOPO-EU-HOOFDST', prerequisites: ['TOPO-EU-LAND'] },
  { familyId: 'TOPO-EU-FYSISCH', prerequisites: ['TOPO-EU-LAND'] },
  { familyId: 'TOPO-EU-ZEEEN', prerequisites: ['TOPO-EU-FYSISCH'] },
  { familyId: 'TOPO-EU-BUURLANDEN', prerequisites: ['TOPO-EU-LAND'] },
  {
    familyId: 'TOPO-EU-RIVIEREN',
    prerequisites: ['TOPO-EU-FYSISCH', 'TOPO-EU-ZEEEN'],
    type: 'soft',
    notes:
      'QF-12: Water als thema eerst — zeeën vóór rivieren (Geobas/Meander volgorde)',
  },
  {
    familyId: 'TOPO-EU-GEBERGTE',
    prerequisites: ['TOPO-EU-FYSISCH', 'TOPO-EU-RIVIEREN'],
    type: 'soft',
    notes: 'QF-12: Rivieren ontspringen in gebergten — rivieren vóór gebergte',
  },
  { familyId: 'TOPO-EU-KLIMAAT', prerequisites: ['TOPO-EU-FYSISCH'] },
  { familyId: 'TOPO-WERELD-CONT', prerequisites: ['TOPO-EU-LAND'] },
  { familyId: 'TOPO-WERELD-OCEANEN', prerequisites: ['TOPO-WERELD-CONT'] },
  { familyId: 'TOPO-WERELD-HOOFDST', prerequisites: ['TOPO-WERELD-CONT'] },
  { familyId: 'TOPO-WERELD-RIVIER', prerequisites: ['TOPO-WERELD-CONT'] },
  { familyId: 'TOPO-WERELD-GEBERGTE', prerequisites: ['TOPO-WERELD-CONT'] },
  { familyId: 'TOPO-WERELD-LANDEN', prerequisites: ['TOPO-WERELD-CONT'] },
  {
    familyId: 'TOPO-WERELD-KLIMAAT',
    prerequisites: ['TOPO-EU-KLIMAAT', 'TOPO-WERELD-CONT'],
  },
  { familyId: 'TOPO-KLIMAAT', prerequisites: ['TOPO-WERELD-KLIMAAT'] },

  // QF-1: TF variant files removed as separate families (Ronde 5).
  // TF questions are now served as part of their base family (TOPO-NL-PROV, TOPO-EU-LAND).
  // No separate LDG edges needed — the questions share the base familyId.
]

// ─── STU — Studievaardigheden (21 families) ──────────────────────────────────

const STU_PREREQUISITES: PrerequisiteDefinition[] = [
  { familyId: 'STU-ALFABET', prerequisites: [] },
  {
    familyId: 'STU-WOORDENBOEK',
    prerequisites: ['STU-ALFABET'],
    type: 'soft',
    notes: 'B3: STU is declaratief — vaardigheden parallel leerbaar',
  },
  {
    familyId: 'STU-BIBLIOTHEEK',
    prerequisites: ['STU-WOORDENBOEK'],
    type: 'soft',
    notes:
      'QF-2: Bibliotheek vóór internet (fysiek zoeken vóór digitaal). Was: STU-INTERNET. B3: soft (declaratief)',
  },
  {
    familyId: 'STU-INTERNET',
    prerequisites: ['STU-BIBLIOTHEEK'],
    type: 'soft',
    notes:
      'QF-2: Internet na bibliotheek. Was: STU-WOORDENBOEK. B3: soft (declaratief)',
  },
  {
    familyId: 'STU-INHOUDSOPGAVE',
    prerequisites: ['STU-ALFABET'],
    type: 'soft',
    notes: 'QF-11: Alfabetische ordening helpt bij inhoudsopgave navigatie',
  },
  {
    familyId: 'STU-TABEL',
    prerequisites: ['STU-INHOUDSOPGAVE'],
    type: 'soft',
    notes:
      'QF-27: Declaratief vak — tabel lezen vereist geen inhoudsopgave-beheersing',
  },
  {
    familyId: 'STU-GRAFIEK',
    prerequisites: ['STU-TABEL'],
    type: 'soft',
    notes: 'B3: STU is declaratief — vaardigheden parallel leerbaar',
  },
  {
    familyId: 'STU-DIAGRAM',
    prerequisites: ['STU-TABEL'],
    type: 'soft',
    notes: 'B3: STU is declaratief — vaardigheden parallel leerbaar',
  },
  {
    familyId: 'STU-STAAF',
    prerequisites: ['STU-GRAFIEK'],
    type: 'soft',
    notes: 'B3: STU is declaratief — vaardigheden parallel leerbaar',
  },
  {
    familyId: 'STU-TIJDLIJN',
    prerequisites: ['STU-TABEL'],
    type: 'soft',
    notes: 'B3: STU is declaratief — vaardigheden parallel leerbaar',
  },
  {
    familyId: 'STU-LIJN',
    prerequisites: ['STU-STAAF'],
    type: 'soft',
    notes: 'B3: STU is declaratief — vaardigheden parallel leerbaar',
  },
  {
    familyId: 'STU-CIRKEL',
    prerequisites: ['STU-STAAF'],
    type: 'soft',
    notes: 'B3: STU is declaratief — vaardigheden parallel leerbaar',
  },
  {
    familyId: 'STU-FLOWCHART',
    prerequisites: ['STU-DIAGRAM'],
    type: 'soft',
    notes: 'B3: STU is declaratief — vaardigheden parallel leerbaar',
  },
  {
    familyId: 'STU-KAART',
    prerequisites: ['STU-TABEL'],
    type: 'soft',
    notes: 'B3: STU is declaratief — vaardigheden parallel leerbaar',
  },
  {
    familyId: 'STU-ROUTE',
    prerequisites: ['STU-KAART'],
    type: 'soft',
    notes: 'B3: STU is declaratief — vaardigheden parallel leerbaar',
  },
  {
    familyId: 'STU-NOTITIES',
    prerequisites: ['STU-TABEL'],
    type: 'soft',
    notes: 'B3: STU is declaratief — vaardigheden parallel leerbaar',
  },
  {
    familyId: 'STU-SAMENVATTEN',
    prerequisites: ['STU-NOTITIES'],
    type: 'soft',
    notes: 'B3: STU is declaratief — vaardigheden parallel leerbaar',
  },
  {
    familyId: 'STU-MINDMAP',
    prerequisites: ['STU-SAMENVATTEN'],
    type: 'soft',
    notes: 'B3: STU is declaratief — vaardigheden parallel leerbaar',
  },
  {
    familyId: 'STU-PLANNEN',
    prerequisites: ['STU-SAMENVATTEN'],
    type: 'soft',
    notes: 'B3: STU is declaratief — vaardigheden parallel leerbaar',
  },
  {
    familyId: 'STU-COMBINEER',
    prerequisites: ['STU-KAART', 'STU-GRAFIEK'],
    type: 'soft',
    notes: 'B3: STU is declaratief — vaardigheden parallel leerbaar',
  },

  // QF-16: STU-KAART → TOPO cross-module link
  {
    familyId: 'STU-KAART',
    prerequisites: ['TOPO-NL-PROV'],
    type: 'soft',
    notes: 'QF-16: Kaartlezen gebruikt geografische basiskennis als context',
  },
]

// ─── Cross-Module Prerequisites (LDG Audit Fase 3) ───────────────────────────

const CROSS_MODULE_PREREQUISITES: PrerequisiteDefinition[] = [
  {
    familyId: 'GELD-KORTING',
    prerequisites: ['GET-PROC-VAN'],
    type: 'soft',
    notes:
      'Cross-module: korting berekenen vereist "percentage van een bedrag" (LDG Audit)',
  },
  {
    familyId: 'STU-SAMENVATTEN',
    prerequisites: ['TAA-LEZEN-KERN'],
    type: 'soft',
    notes:
      'Cross-module: samenvatten vereist hoofdgedachte herkennen (LDG Audit)',
  },
  {
    familyId: 'GELD-KOMMA',
    prerequisites: ['GET-KOMMA-PLUS'],
    type: 'soft',
    notes:
      'Cross-module: geld rekenen met kommagetallen vereist optellen/aftrekken met kommas (LDG Audit)',
  },
  {
    familyId: 'REK-CIJF-PLUS',
    prerequisites: ['GET-GROOT-POS'],
    type: 'soft',
    notes: 'Cross-module: positiestelsel nodig voor cijferend rekenen',
  },
  {
    familyId: 'MET-OPP-RECHT',
    prerequisites: ['REK-TAFELS-TOEPASSEN'],
    type: 'soft',
    notes: 'Cross-module: tafels nodig voor oppervlakte berekenen',
  },
  {
    familyId: 'GELD-PRIJS-PER',
    prerequisites: ['REK-CONTEXT-KEER-DEEL'],
    type: 'soft',
    notes: 'Cross-module: contextrekenen nodig voor prijs-per-eenheid',
  },
  {
    familyId: 'GET-KOMMA-KEER',
    prerequisites: ['REK-CIJF-KEER'],
    type: 'soft',
    notes: 'Cross-module: cijferend keer nodig voor komma-keer',
  },
  {
    familyId: 'GET-KOMMA-DEEL',
    prerequisites: ['REK-CIJF-DEEL'],
    type: 'soft',
    notes: 'Cross-module: cijferend delen nodig voor komma-delen',
  },
  {
    familyId: 'GET-MACHT',
    prerequisites: ['REK-VOLGORDE'],
    type: 'soft',
    notes:
      'Cross-module: volgorde bewerkingen nodig voor machten (was GET-VOLGORDE-BEWERKING)',
  },
  {
    familyId: 'REK-CONTEXT-PLUS-MIN',
    prerequisites: ['TAA-LEZEN-KERN'],
    type: 'soft',
    notes:
      'Cross-module: begrijpend lezen nodig voor contextopgaven (audit R2)',
  },

  // QF-7: GELD hogere families → GET-PROC cross-links
  {
    familyId: 'GELD-RENTE-PROC',
    prerequisites: ['GET-PROC-SAMENGESTELD'],
    type: 'soft',
    notes: 'QF-7: Samengestelde rente IS samengesteld procentrekenen',
  },
  {
    familyId: 'GELD-TERUGREKENEN',
    prerequisites: ['GET-PROC-TERUG'],
    type: 'soft',
    notes: 'QF-7: Terugrekenen bij korting = omgekeerd procentrekenen',
  },
  {
    familyId: 'GELD-BTW',
    prerequisites: ['GET-PROC-VAN'],
    type: 'soft',
    notes: 'QF-7: BTW berekenen = percentage van bedrag',
  },

  // QF-11: Additional cross-module links
  {
    familyId: 'MET-TIJD-METEN',
    prerequisites: ['TIJD-ANALOOG'],
    type: 'soft',
    notes: 'QF-11: Kloklezen (TIJD) als basis voor tijdmeten (MET)',
  },
  {
    familyId: 'REK-TAFELS-BASIS',
    prerequisites: ['REK-CIJF-PLUS'],
    type: 'soft',
    notes: 'QF-11: Vermenigvuldiging = herhaald optellen',
  },
  // Note: TIJD-KALENDER prereq TIJD-OMREKENEN is in TIJD_PREREQUISITES (intra-module, not cross-module)
  // Note: STU-INHOUDSOPGAVE prereq STU-ALFABET is in STU_PREREQUISITES (intra-module, not cross-module)

  // QF-17: GET-VERH-SCHAAL-KAART → MET-SCHAAL cross-module link
  {
    familyId: 'GET-VERH-SCHAAL-KAART',
    prerequisites: ['MET-SCHAAL-BASIS'],
    type: 'soft',
    notes: 'QF-17: Schaal op kaarten (GET) vereist schaal-begrip (MET)',
  },
]

// ─── Combined Export ──────────────────────────────────────────────────────────

export const ALL_PREREQUISITES: PrerequisiteDefinition[] = [
  ...GET_PREREQUISITES,
  ...REK_PREREQUISITES,
  ...MET_PREREQUISITES,
  ...TIJD_PREREQUISITES,
  ...GELD_PREREQUISITES,
  ...SWW_PREREQUISITES,
  ...SPR_PREREQUISITES,
  ...TAA_PREREQUISITES,
  ...TOPO_PREREQUISITES,
  ...STU_PREREQUISITES,
  ...CROSS_MODULE_PREREQUISITES,
]

/** Module-specific exports for testing */
export {
  GET_PREREQUISITES,
  REK_PREREQUISITES,
  MET_PREREQUISITES,
  TIJD_PREREQUISITES,
  GELD_PREREQUISITES,
  SWW_PREREQUISITES,
  SPR_PREREQUISITES,
  TAA_PREREQUISITES,
  TOPO_PREREQUISITES,
  STU_PREREQUISITES,
  CROSS_MODULE_PREREQUISITES,
}
